import { useEffect, useId, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../../shell/session";
import { Empty, ErrorNote } from "../../ui";
import type { ServiceType } from "../../../shared/types";
import { createPlan, listServiceTypes } from "./client";
import { canLead, defaultPlanStart, localInputToUtc, messageOf, stageColor } from "./model";
import "./services.css";

export function PlanCreatePage() {
  const { user, loading } = useSession();
  const navigate = useNavigate();
  const titleId = useId();
  const whenId = useId();
  const seriesId = useId();
  const notesId = useId();
  const publicId = useId();
  const [types, setTypes] = useState<ServiceType[]>([]);
  const [title, setTitle] = useState("Sunday gathering");
  const [serviceTypeId, setServiceTypeId] = useState("");
  const [when, setWhen] = useState(defaultPlanStart);
  const [series, setSeries] = useState("");
  const [status, setStatus] = useState<"draft" | "confirmed">("draft");
  const [notes, setNotes] = useState("");
  const [publicNotes, setPublicNotes] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    void listServiceTypes()
      .then((rows) => {
        if (cancel) return;
        setTypes(rows);
        setServiceTypeId(rows.find((type) => type.name === "Sunday Gathering")?.id ?? rows[0]?.id ?? "");
      })
      .catch((caught: unknown) => { if (!cancel) setError(messageOf(caught)); });
    return () => { cancel = true; };
  }, []);

  if (loading) return <section className="page svc">Loading…</section>;
  if (!canLead(user?.role)) {
    return (
      <section className="page svc">
        <Empty title="A leader creates the plan" body="You can open the board and respond to your own assignments." action={<Link className="btn" to="/services">All plans</Link>} />
      </section>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const startsAt = localInputToUtc(when);
    if (!title.trim()) { setError("Give the plan a title."); return; }
    if (!serviceTypeId) { setError("Choose a service type."); return; }
    if (!startsAt) { setError("Enter a valid Auckland date and time."); return; }
    setBusy(true);
    setError("");
    try {
      const plan = await createPlan({
        title: title.trim(),
        serviceTypeId,
        startsAt,
        status,
        notes,
        publicNotes,
        series: series.trim() ? series.trim() : null,
      });
      navigate(`/services/${plan.id}`);
    } catch (caught) {
      setError(messageOf(caught));
      setBusy(false);
    }
  }

  return (
    <section className="page svc">
      <header className="page-head">
        <div>
          <p className="kicker">Services</p>
          <h1 className="page-title">New plan</h1>
          <p className="lede">The service type copies its teams onto this plan. You can add the order as soon as it exists.</p>
        </div>
        <Link className="btn ghost" to="/services">All plans</Link>
      </header>
      {error && <ErrorNote>{error}</ErrorNote>}
      <form className="svc-split" onSubmit={(event) => void submit(event)}>
        <div className="panel panel-pad form-grid">
          <div className="field"><label htmlFor={titleId}>Title</label><input id={titleId} value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} required /></div>
          <div className="field"><label htmlFor={whenId}>Starts (Auckland)</label><input id={whenId} type="datetime-local" value={when} onChange={(event) => setWhen(event.target.value)} required /></div>
          <div className="field"><label htmlFor={seriesId}>Series</label><input id={seriesId} value={series} maxLength={160} onChange={(event) => setSeries(event.target.value)} placeholder="Optional" /></div>
          <div className="field">
            <label htmlFor="plan-status">Status</label>
            <select id="plan-status" value={status} onChange={(event) => setStatus(event.target.value === "confirmed" ? "confirmed" : "draft")}>
              <option value="draft">Draft</option>
              <option value="confirmed">Confirmed</option>
            </select>
          </div>
          <div className="field"><label htmlFor={notesId}>Team notes</label><textarea id={notesId} value={notes} maxLength={8000} onChange={(event) => setNotes(event.target.value)} /></div>
          <div className="field"><label htmlFor={publicId}>Public notes</label><textarea id={publicId} value={publicNotes} maxLength={8000} onChange={(event) => setPublicNotes(event.target.value)} /></div>
          <button className="btn primary" type="submit" disabled={busy || types.length === 0}>{busy ? "Creating…" : "Create plan"}</button>
        </div>
        <fieldset className="panel panel-pad">
          <legend>Service type</legend>
          {types.length === 0 && <p className="muted">No service types are available, so a plan cannot be created.</p>}
          {types.map((type) => (
            <label className={serviceTypeId === type.id ? "svc-type on" : "svc-type"} key={type.id}>
              <input type="radio" name="serviceTypeId" value={type.id} checked={serviceTypeId === type.id} onChange={() => setServiceTypeId(type.id)} />
              <span className="svc-type-bar" style={{ background: stageColor(type.color) }} />
              <span><strong>{type.name}</strong><small className="faint">{type.description || "Teams for this type are copied onto the plan."}</small></span>
            </label>
          ))}
        </fieldset>
      </form>
    </section>
  );
}
